import { Card as MantineCard, Divider as MantineDivider, Paper as MantinePaper, ScrollArea as MantineScrollArea } from "@mantine/core";
import * as React from "react";

import type { ElementProps, SizeValue, StyleProps } from "./style-props";

import classes from "./surfaces.module.css";

export interface PaperProps extends ElementProps<"div">, StyleProps {
  /** Non-numeric: Mantine resolves shadows by theme key only, so a number would silently produce no shadow. */
  shadow?: Exclude<SizeValue, number>;
  radius?: SizeValue;
  withBorder?: boolean;
  /**
   * The root element. Open for the same reason `TextProps.component` is: the one non-div call site
   * (`AssistantChat`) renders the surface as a `<button>`, and `type`/`disabled` below exist for it.
   */
  component?: React.ElementType;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
}

/**
 * Same flattening as `TextRoot` in the typography module: Mantine types `Paper` as a polymorphic union
 * whose `component` arm selects the element prop set, and a non-generic wrapper cannot restate
 * that. `surfaces-casts.test.ts` pins what it can: that the named prop *keys* remain keys of
 * Mantine's own PaperProps/CardProps. It cannot pin `component` (supplied by the polymorphic
 * factory, not the props type), `type`/`disabled` (element-level, reaching the DOM through
 * Mantine's rest-prop spread), or a value-type narrowing that keeps the key — those rest on the
 * cast alone.
 */
const PaperRoot = MantinePaper as React.ComponentType<PaperProps & { readonly ref?: React.Ref<HTMLDivElement> }>;

export const Paper = React.forwardRef<HTMLDivElement, PaperProps>(function Paper({ children, ...props }, ref) {
  return (
    <PaperRoot {...props} ref={ref}>
      {children}
    </PaperRoot>
  );
});

export interface CardProps extends ElementProps<"div">, StyleProps {
  /** Non-numeric — see PaperProps.shadow. */
  shadow?: Exclude<SizeValue, number>;
  radius?: SizeValue;
  /** Mantine 6 spells Card's own padding `padding`; the `p` style prop is also live at call sites. */
  padding?: SizeValue;
  withBorder?: boolean;
}

// `Card.Section` has zero call sites outside `src/ui`, so it is deliberately not exported — a
// consumer that needs it adds it here first.
const CardRoot = MantineCard as React.ComponentType<CardProps & { readonly ref?: React.Ref<HTMLDivElement> }>;

/**
 * Mantine 6's Card was a block Paper; Mantine 7's is a column flex container, which stretches and
 * blockifies its children (SC-2703). The wrapper keeps `display: block` unless the call site sets a
 * display, through the `display` style prop or `style` — a style prop outranks `style` in Mantine 7,
 * so the default yields to either.
 */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card({ children, display, ...props }, ref) {
  return (
    <CardRoot {...props} display={display ?? props.style?.display ?? "block"} ref={ref}>
      {children}
    </CardRoot>
  );
});

export interface DividerProps extends ElementProps<"div">, StyleProps {
  color?: string;
  label?: React.ReactNode;
  labelPosition?: "left" | "center" | "right";
  orientation?: "horizontal" | "vertical";
  size?: SizeValue;
}

/** The label slot class of an uncoloured Divider; see `surfaces.module.css`. */
const UNCOLORED_DIVIDER_CLASS_NAMES = { label: classes.uncoloredDividerLabel };

/**
 * Mantine 6 drew an uncoloured Divider in `gray.4` and put its label on the left; Mantine 7 draws it
 * in `gray.3` and centres the label. Mantine 6 also left an uncoloured Divider's label in the inherited
 * text color, where Mantine 7 dims it. The wrapper keeps the Mantine 6 defaults (SC-2703).
 */
export const Divider = React.forwardRef<HTMLDivElement, DividerProps>(function Divider({ color, labelPosition = "left", ...props }, ref) {
  return (
    <MantineDivider
      {...props}
      color={color ?? "gray.4"}
      labelPosition={labelPosition}
      classNames={color === undefined ? UNCOLORED_DIVIDER_CLASS_NAMES : undefined}
      ref={ref}
    />
  );
});

// `dir` is omitted because Mantine's own ScrollArea narrows it to `"ltr" | "rtl"`, which the
// wider `div` attribute type cannot satisfy; no call site passes it.
export interface ScrollAreaProps extends Omit<ElementProps<"div">, "dir">, StyleProps {
  type?: "auto" | "scroll" | "always" | "never" | "hover";
  scrollbarSize?: number | string;
  offsetScrollbars?: boolean;
  viewportRef?: React.Ref<HTMLDivElement>;
  onScrollPositionChange?: (position: { x: number; y: number }) => void;
}

const ScrollAreaRoot = React.forwardRef<HTMLDivElement, ScrollAreaProps>(function ScrollAreaRoot({ children, ...props }, ref) {
  return (
    <MantineScrollArea {...props} ref={ref}>
      {children}
    </MantineScrollArea>
  );
});

// `mah` — the prop every Autosize call site pairs it with — comes from `StyleProps`.
const ScrollAreaAutosize = React.forwardRef<HTMLDivElement, ScrollAreaProps>(function ScrollAreaAutosize({ children, ...props }, ref) {
  return (
    <MantineScrollArea.Autosize {...props} ref={ref}>
      {children}
    </MantineScrollArea.Autosize>
  );
});

export const ScrollArea = Object.assign(ScrollAreaRoot, { Autosize: ScrollAreaAutosize });
